import { Toast } from 'antd-mobile'
import axios, { InternalAxiosRequestConfig, AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import Cookies from 'js-cookie'
import { store } from '@/store/modules'
import { loginStateAtom, setLoginStateAtom, userAtom, globalLoadingAtom, refreshSocketAtom, isBanModalVisible, banInfoAtom, BanInfo } from '@/store'
import { getCookieAuthInfo, getGlobalConfig, getOrCreateSensorsAnonymousId, isPWA, jumpMainLogin, setCookieAuthInfo } from '@/utils'
import { ApiResult, EErrorCode } from '.'
import { BAN_TYPE_ENUM } from '@/types'

export type TLogin = {
  /**
   * 过期时间
   */
  expire: string
  /**
   * 刷新token
   */
  refresh_token: string
  /**
   * jwt
   */
  token: string
}

const baseURL = getGlobalConfig().VITE_GLOB_API_URL

const domainMap: Record<string, string> = {
  '/imApi': getGlobalConfig().VITE_IM_API_URL,
  '/taskApi': getGlobalConfig().VITE_TASK_API_URL,
  '/cmtyApi': getGlobalConfig().VITE_CMTY_API_URL,
  // 添加更多的映射关系
  default: getGlobalConfig().VITE_GLOB_API_URL, // 默认域名
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  timeout: 10 * 60 * 1000,
})

// 存储 pending 请求的取消函数
const pendingRequests: Map<string, () => void> = new Map()

let isRefreshing: boolean = false
let refreshSubscribers: Array<(token: string) => void> = []
let refreshFailSubscribers: Array<() => void> = []
let refreshError: Error | null = null

// 取消所有 pending 的请求
function cancelAllPendingRequests(reason?: string): void {
  pendingRequests.forEach((cancel) => cancel())
  pendingRequests.clear()
  console.log(`[axios] Cancelled all pending requests${reason ? `: ${reason}` : ''}`)
}

// 添加 pending 请求
function addPendingRequest(url: string, cancel: () => void): void {
  pendingRequests.set(url, cancel)
}

// 移除 pending 请求
function removePendingRequest(url: string): void {
  pendingRequests.delete(url)
}

function subscribeTokenRefresh(cb: (token: string) => void): void {
  refreshSubscribers.push(cb)
}

function subscribeRefreshFail(cb: () => void): void {
  refreshFailSubscribers.push(cb)
}

function onRefreshed(token: string): void {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
  refreshFailSubscribers = []
  refreshError = null
}

function onRefreshFailed(err?: Error): void {
  const errToNotify = err || new Error('Token refresh failed')
  refreshError = errToNotify
  refreshFailSubscribers.forEach((cb) => cb())
  refreshSubscribers = []
  refreshFailSubscribers = []
}

// // 用于处理401重试的请求队列
// const retryRequestQueue: Array<{
//   config: InternalAxiosRequestConfig
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   resolve: (value: any) => void
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   reject: (reason: any) => void
// }> = []

// let isHandling401 = false

async function refreshTokenFetch(refreshToken: string): Promise<TLogin | null> {
  try {
    const response = await fetch(`${baseURL}/user/refreshToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
      body: JSON.stringify({}), // 如果需要请求体，可以在这里添加
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    if (data.code === 0) {
      store.set(refreshSocketAtom, true)
      return data.data // 返回新的 token
    } else {
      // 处理刷新 token 失败的情况，比如跳转到登录页
      Toast.show({
        content: 'Login Failure',
        position: 'top',
      })
      store.set(setLoginStateAtom, false)

      jumpMainLogin()
      return null
    }
  } catch (error) {
    // 处理网络错误或请求失败的情况
    console.error('Failed to refresh token:', error)
    Toast.show({
      content: 'Login Failure',
      position: 'top',
    })
    store.set(setLoginStateAtom, false)

    jumpMainLogin()
    return null
  }
}

// 添加请求拦截器
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const requestKey = `${config.method}_${config.url}`

    // 注册取消函数到 pending 队列
    const cancelToken = axios.CancelToken.source()
    config.cancelToken = cancelToken.token
    addPendingRequest(requestKey, () => cancelToken.cancel('Request cancelled'))

    const { token, tokenExpire, refreshToken } = getCookieAuthInfo('token', 'tokenExpire', 'refreshToken')

    // 添加token到请求头中
    if (token && config.url !== '/user/refreshToken') {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 取消请求缓存
    config.headers['Cache-Control'] = 'no-cache'

    // 添加渠道参数到请求头
    config.headers['APP-CHANNEL'] = 'wap'
    config.headers['FB-FBC'] = Cookies.get('_fbc')
    config.headers['FB-FBP'] = Cookies.get('_fbp')
    config.headers['TK-TTP'] = Cookies.get('_ttp')
    config.headers['TK-TTC'] = Cookies.get('ttclid')
    config.headers['PWA'] = Number(isPWA())

    if (store.get(loginStateAtom)) {
      const userInfo = store.get(userAtom)
      config.headers['SENSOR-DISTINCT-ID'] = userInfo?.id
    } else {
      const anonymousId = getOrCreateSensorsAnonymousId()
      if (anonymousId) {
        config.headers['SENSOR-DISTINCT-ID'] = anonymousId
      }
    }

    // 根据请求路径动态设置 baseURL
    const path = config.url?.split('/')[1]
    const domain = domainMap[`/${path}`] || domainMap['default']
    config.baseURL = domain

    // 获取token
    if (token && tokenExpire && !['/user/refreshToken', '/user/login', '/resource/discovery', '/resource/story'].includes(config.url || '')) {
      const now: number = new Date().getTime()
      const expireTime: number = Number(tokenExpire) * 1000

      // 提前1小时续签（3600秒 = 3600000毫秒）
      if (now >= expireTime - 3600 * 1000 && refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true

          try {
            const response = await refreshTokenFetch(refreshToken || '')
            if (response) {
              const { token: newToken, refresh_token, expire: tokenExpire } = response
              setCookieAuthInfo('token', newToken, {
                expires: new Date(Number(tokenExpire) * 1000),
              })
              setCookieAuthInfo('refreshToken', refresh_token)
              setCookieAuthInfo('tokenExpire', tokenExpire)
              store.set(setLoginStateAtom, true)
              onRefreshed(newToken)
            } else {
              onRefreshFailed(new Error('Refresh token returned null'))
              isRefreshing = false
              Toast.show({ content: 'Login Failure', position: 'top' })
              store.set(setLoginStateAtom, false)
              jumpMainLogin()
            }
          } catch (error) {
            console.error('Error during token refresh:', error)
            onRefreshFailed(error instanceof Error ? error : new Error(String(error)))
            isRefreshing = false
            Toast.show({ content: 'Login Failure', position: 'top' })
            store.set(setLoginStateAtom, false)
            jumpMainLogin()
          }
        }

        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            config.headers['Authorization'] = `Bearer ${token}`
            resolve(config)
          })
          subscribeRefreshFail(() => {
            reject(refreshError || new Error('Token refresh failed'))
          })
        })
      }
    }

    return config
  },
  (error: unknown) => {
    // 处理请求错误
    return Promise.reject(error)
  },
)

// 检查并处理封禁状态
function checkBanStatus(response: AxiosResponse): void {
  const banInfo = response.data?.data?.banInfo
  if (!banInfo?.length) return

  // 检查所有封禁类型
  const banCommunityIdx = banInfo.findIndex((item: BanInfo) => item.type === BAN_TYPE_ENUM.BAN_COMMUNITY)

  const banItem = banInfo[banCommunityIdx]

  if (banCommunityIdx !== -1) {
    store.set(isBanModalVisible, true)
    store.set(banInfoAtom, [banItem])
    // 取消所有 pending 的请求
    cancelAllPendingRequests('User banned')
  }
}

// 添加响应拦截器
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    if (response.config.url === '/user/detail/me' && response.data?.code === EErrorCode.USER_NOT_FOUND) {
      store.set(setLoginStateAtom, false)
    }
    // 检查所有封禁类型
    checkBanStatus(response)
    // 请求完成，移除 pending
    removePendingRequest(`${response.config.method}_${response.config.url}`)
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    store.set(globalLoadingAtom, false)
    // 请求完成/失败，移除 pending
    if (originalRequest) {
      removePendingRequest(`${originalRequest.method}_${originalRequest.url}`)
    }

    // 如果是取消的请求，直接返回 rejected
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // 封禁登录 code === -1005 HTTP status 401
    const { token } = getCookieAuthInfo('token')
    const errResponseData = error.response?.data as ApiResult<null>
    if (error.response?.status === 401 && errResponseData && errResponseData.code === EErrorCode.UNAUTHORIZED && token) {
      await new Promise<void>((resolve) => {
        Toast.show({
          content: errResponseData.message,
          position: 'top',
          duration: 3000,
          afterClose: () => {
            store.set(setLoginStateAtom, false)
            resolve()
          },
        })
      })

      jumpMainLogin()
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      // 如果当前token过期或者cookie中token不存在时,并且存在refreshToken情况下，尝试更新token

      const { refreshToken } = getCookieAuthInfo('refreshToken')

      if (!refreshToken) {
        await new Promise<void>((resolve) => {
          Toast.show({
            position: 'top',
            content: 'Login to continue',
            duration: 1500,
            afterClose: resolve,
          })
        })

        store.set(setLoginStateAtom, false)
        jumpMainLogin()
        return Promise.reject(error)
      }

      // 成功后将重新发起请求队列，失败后再跳主应用登录
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            resolve(axiosInstance(originalRequest))
          })
        })
      }
      isRefreshing = true

      try {
        const response = await refreshTokenFetch(refreshToken)
        if (!response) {
          await new Promise<void>((resolve) => {
            Toast.show({
              position: 'top',
              content: 'Login to continue',
              duration: 1500,
              afterClose: resolve,
            })
          })

          store.set(setLoginStateAtom, false)
          jumpMainLogin()
          return Promise.reject(error)
        }

        const { token: newToken, refresh_token, expire } = response

        // 保存新的 token 信息
        setCookieAuthInfo('token', newToken, {
          expires: new Date(Number(expire) * 1000),
        })
        setCookieAuthInfo('refreshToken', refresh_token)
        setCookieAuthInfo('tokenExpire', expire)
        store.set(setLoginStateAtom, true)

        // 通知所有等待的请求使用新 token
        onRefreshed(newToken)

        // 更新当前请求的 header 并重试
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`
        return axiosInstance(originalRequest)
      } catch (refreshError) {
        // 刷新 token 过程中出现错误
        console.error('Error during token refresh:', refreshError)

        await new Promise<void>((resolve) => {
          Toast.show({
            position: 'top',
            content: 'Login expired, please login again',
            duration: 1500,
            afterClose: resolve,
          })
        })

        store.set(setLoginStateAtom, false)
        jumpMainLogin()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 处理其他错误
    if (error.code !== 'ERR_CANCELED' && error.response?.status !== 401) {
      Toast.show({
        content: 'Request Failed',
        position: 'top',
      })
    }
    return Promise.reject(error)
  },
)

export default axiosInstance
